import {PolymerElement, html} from '@polymer/polymer';
import '@polymer/polymer/lib/elements/dom-if.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import {timeOut} from '@polymer/polymer/lib/utils/async.js';
import {ResizableBehavior} from '../behaviors/resizable-behavior.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {ResizeObserverBehavior} from '../behaviors/resize-observer-behavior.js';

class KeliBrowse extends ScopeBinding(ResizableBehavior(HotkeyBehavior(ResizeObserverBehavior(PolymerElement)))) {
  static get template() {
    return html`
<style>
  :host {
    outline: none;
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: visible;
    font-weight: 400;
    font-size: 14px;
    height: var(--keli-browse-height);
    min-height: var(--keli-browse-min-height);
    max-height: var(--keli-browse-height, var(--keli-browse-max-height));

    --keli-text-dark: #000000;
    --keli-text-light: #FFFFFF;
  }

  :host(.resizeToContent) {
    min-height: 0 !important;
    height: auto !important;
    max-height: none !important;
  }

  .cell, keli-browse-row {
    font-size: 15px;
    letter-spacing: -.5px;
  }

  .cell sl-icon, keli-browse-row .cell sl-icon {
    font-size: 16px;
    display: block;
    margin: auto;
  }

  #browseHeaderScroll, #browseFooterScroll {
    width: 100%;
    overflow: hidden;
  }

  #browseBodyScroll {
    flex: 1;
    width: 100%;
    overflow: auto;
    position: relative;
    outline: none;
  }

  #browseHeader {
    display: grid;
    grid-template-columns: auto 1fr;
    color: var(--keli-text);
    background-color: var(--keli-browse-header-background);
  }

  #browseHeader sl-icon {
    font-size: 18px;
    padding: 2px 6px;
    cursor: pointer;
  }

  #browseHeaderCaption {
    text-align: center;
    font-size: 14px;
    line-height: 24px;
    font-weight: 700;
  }

  #header {
    min-width: 100%;
    resize: none;
    display: block;
    white-space: nowrap;
    color: var(--keli-text);
    background-color: var(--keli-browse-toolbar-background);
    border-bottom: 1px solid var(--keli-browse-toolbar-border-color);
  }

  #body {
    min-width: 100%;
    min-height: 1px; /* Horizontal scrolling, when there are no rows. */
    resize: none;
    display: block;
    -webkit-touch-callout: none;
    user-select: none;
    position: relative;
  }

  #footer {
    min-width: 100%;
    resize: none;
    display: block;
    white-space: nowrap;
    color: var(--keli-text);
    background-color: var(--keli-browse-toolbar-background);
    border-top: 1px solid var(--keli-browse-toolbar-border-color);
  }

  #browseHeaderScroll {
    min-height: 18px;
  }

  :host(.filterable) #browseHeaderScroll {
    min-height: 36px;
  }

  #footer, #browseFooterScroll, #footerControls {
    min-height: 18px;
  }

  #footerControls {
    position: absolute;
    bottom: 0;
    right: 0;
    display: inline-block;
    white-space: nowrap;
    height: 18px;
    padding-left: 2px;
    background-color: var(--keli-browse-toolbar-background);
    border-left: 1px solid var(--keli-browse-toolbar-border-color);
  }

  #footerControls.hidden {
    display: none;
  }

  #footerControls sl-icon {
    cursor: pointer;
    font-size: 18px;
    color: var(--keli-text);
  }

  #shortcut {
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    display: inline-block;
    vertical-align: top;
  }

  #addBrowseRow, #removeBrowseRow {
    display: none !important;
  }

  #addBrowseRow.visible, #removeBrowseRow.visible {
    display: inline-block !important;
  }

  #modifyColumns {
    position: relative;
    z-index: var(--keli-zindex-browse-menuicon);
  }

  #modifyColumnsMenu {
    z-index: var(--keli-zindex-browse-menu);
  }

  #resizeBottom {
    position: absolute;
    min-width: 8px;
    min-height: 8px;
    opacity: 0;
    cursor: ns-resize;
    bottom: -3px;
    right: 0;
    left: 0;
    display: none;
  }

  #editRow {
    z-index: var(--keli-zindex-browse-edit);
  }

  #editRow {
    display: none;
    min-height: 24px;
    max-height: 24px;
  }

  #editRow.visible {
    display: table;
  }

  keli-browse-row.selected {
    background-image: var(--keli-browse-row-focused-background-image);
    background-repeat: no-repeat;
    background-position: 1px center;
  }

  :host(.focused) keli-browse-row.selected {
    box-shadow: inset -1px 0 0 2px var(--keli-browse-row-focused-border-active);
    background-color: var(--keli-browse-row-focused-background);
  }
</style>

<keli-browse-controller id="controller" keliname="_t"></keli-browse-controller>

<div id="browseHeader">
  <div id="browseHeaderOptions">
    <sl-icon id="addBrowseRow" name="add" title="Add a record" on-click="handleAddBrowseRowClick"></sl-icon>
    <sl-icon id="removeBrowseRow" name="remove" title="Remove selected record" on-click="handleRemoveBrowseRowClick"></sl-icon>
  </div>
  <div id="browseHeaderCaption">[[caption]]</div>
</div>
<div id="browseHeaderScroll" on-scroll="handleBrowseHeaderScroll">
  <div id="header"></div>
</div>
<div id="browseBodyScroll" on-scroll="handleBrowseBodyScroll">
  <keli-browse-blank-slate id="blankSlate"></keli-browse-blank-slate>
  <div id="body">
    <keli-browse-edit-row id="editRow"></keli-browse-edit-row>
  </div>
</div>
<div id="browseFooterScroll" on-scroll="handleBrowseFooterScroll">
  <div id="footer"></div>
  <div id="footerControls">
    <template is="dom-if" if="[[formattedShortcutKey]]">
      <span id="shortcut">[[formattedShortcutKey]]</span>
    </template>
    <sl-icon id="modifyColumns" name="view-column" title="Manage Columns" on-click="handleModifyColumnsClick"></sl-icon>
    <keli-browse-configuration-menu id="modifyColumnsMenu"></keli-browse-configuration-menu>
  </div>
</div>
<div id="resizeBottom"></div>`;
  }

  static get is() {
    return 'keli-browse';
  }

  static get properties() {
    // @formatter:off
    return {
      AdjacentElementFilterFields: Object,
      blankSlate: {type: Boolean, scopevar: true, observer: 'blankSlateChanged'},
      browseControlCreateVisible: {type: Boolean, scopevar: true, observer: 'browseControlCreateVisibleChanged'},
      browseControlDeleteVisible: {type: Boolean, scopevar: true, observer: 'browseControlDeleteVisibleChanged'},
      caption: {type: String, scopevar: true},
      chooseOnFilteringComplete: Boolean,
      currentlyFiltering: {type: Boolean, value: false},
      currentlySorting: {type: Boolean, value: false},
      currentSortOrder: Array,
      disableBrowseConfiguration: {type: Boolean, value: false, observer: 'disableBrowseConfigurationChanged'},
      disabled: {type: Boolean, value: false},
      editing: {type: Boolean, scopevar: true, observer: 'editingChanged'},
      eip: {type: Boolean, scopevar: true},
      filterable: {type: Boolean, value: true, observer: 'filterableChanged', scopevar: true},
      focused: {type: Boolean, observer: 'focusedChanged'},
      footerControls: {type: Boolean, scopevar: true, observer: 'footerControlsChanged'},
      formattedShortcutKey: {type: String, computed: 'computeFormattedShortcutKey(shortcutKey)'},
      height: {type: String, scopevar: {options: {view: true}}, observer: 'heightChanged'},
      isBlockPanelElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['datatable']},
      multiLine: {type: Boolean, scopevar: true, observer: 'multiLineChanged'},
      needsDataLoaded: {type: Boolean, value: true},
      readOnly: {type: Boolean, scopevar: 'ro', observer: 'readOnlyChanged'},
      resizableByUser: {type: Boolean, observer: 'resizableByUserChanged'},
      resizeToContent: {type: Boolean, scopevar: true, observer: 'resizeToContentChanged'},
      rowHeight: {type: Number, value: 24},
      rows: {type: Number, observer: 'rowsChanged'},
      selectedRow: {type: Object, observer: 'selectedRowChanged'},
      selectedRowNumber: {type: Number, observer: 'selectedRowNumberChanged'},
      shortcutKey: String,
      skip: {type: Boolean, scopevar: true},
      sortable: {type: Boolean, value: true, scopevar: true},
      width: {type: String, scopevar: true, observer: 'widthChanged'},
      dataSettled: {type: Boolean, computed: 'computeDataSettled(currentlyFiltering, currentlySorting, selectedRow)', observer: 'dispatchDataSettled'}
    }
    // @formatter:on
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliFocus', 'keliBlur', 'keliBrowseRowActivated', 'keliBrowseRowSelected', 'keliMultiSelectChanged', 'keliMultiSelectFilterToAll']);
  }

  connectedCallback() {
    super.connectedCallback();
    this._ensureAttribute('tabindex', '0');
    this.classList.add('focusable-control');

    if (this.filterable !== false) {
      this.classList.add('filterable');
    }

    this.setTableWidth();
    this.dispatchEvent(new CustomEvent('panelElementCreated', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));

    this.resizableParentElement = this;
    this.resizableHandleElements = [
      {element: this.$.resizeBottom, direction: 's'}
    ];

    this.addEventListener('activateRow', this.handleActivateRow);
    this.addEventListener('blur', this.handleBlur);
    this.addEventListener('browseHeaderCellActivated', this.handleBrowseHeaderCellActivated);
    this.addEventListener('browseHeaderCellAttached', this.handleBrowseHeaderCellAttached);
    this.addEventListener('browseHeaderCellDetached', this.handleBrowseHeaderCellDetached);
    this.addEventListener('browseHeaderCellFilterableChanged', this.handleBrowseHeaderCellFilterableChanged);
    this.addEventListener('browsePageLoaded', this.handleBrowsePageLoaded);
    this.addEventListener('click', this.enterEditMode);
    this.addEventListener('columnJustifyChanged', this.handleColumnJustifyChanged);
    this.addEventListener('columnWidthChanged', this.handleColumnWidthChanged);
    this.addEventListener('filterInputBlurred', this.handleFilterInputBlurred);
    this.addEventListener('focus', this.handleFocus);
    this.addEventListener('focusElement', this.handleFocusElement);
    this.addEventListener('keliBlur', this.handleEditableFieldBlur);
    this.addEventListener('keliMultiSelectChanged', this.handleKeliMultiSelectChanged);
    this.addEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.addEventListener('setSelectedRow', this.handleSetSelectedRow);
    this.addEventListener('sortByColumn', this.handleSortByColumn);


    this.addHotkeyListener('raw-listener', this, {forceDefaults: true});
    this.registerHotkey(KeliInterface.Hotkeys.browse.choose, {
      down: (event) => {
        if (this.currentlyFiltering) {
          this.chooseOnFilteringComplete = true;
          return;
        }
        if (this.selectedRow) {
          this.selectedRow.activateRow(event);
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.browse.previous, {
      down: (event) => {
        if (this.selectedRowNumber !== 1) {
          event.preventDefault();
          event.stopPropagation();
          this.movePosition(-1);
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.browse.next, {
      down: (event) => {
        if (this.selectedRowNumber !== this.rows) {
          event.preventDefault();
          event.stopPropagation();
          this.movePosition(1);
        }
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.browse.previousPage, {
      down: () => {
        this.movePosition(-1, true);
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.browse.nextPage, {
      down: () => {
        this.movePosition(1, true);
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.browse.first, {
      down: () => {
        this.movePosition(null, null, 1);
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.browse.last, {
      down: () => {
        if (this.rows) {
          this.movePosition(null, null, this.rows);
        }
      }
    });

    this.AdjacentElementFilterFields = new AdjacentElement({
      'this': this.$.header,
      'selector': 'keli-browse-header-cell',
      'properties': {filterable: [true, undefined], skip: [false, undefined]},
      'wrap': false,
      'defaultCallback': (newElement) => {
        if (newElement && newElement.focusElement && !this.editing && !this.$.modifyColumnsMenu?.opened) {
          newElement.focusElement();
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.browse.filter.focusFirst, {
      down: () => {
        this.AdjacentElementFilterFields.first();
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.browse.filter.next, {
      down: (event) => {
        if (this.AdjacentElementFilterFields.isLast) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.AdjacentElementFilterFields.next();
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.browse.filter.previous, {
      down: (event) => {
        if (this.AdjacentElementFilterFields.isFirst) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.AdjacentElementFilterFields.previous();
      }
    }, 'raw-listener');
    this.registerHotkey(KeliInterface.Hotkeys.browse.edit.toggle, {
      down: () => {
        if (this.readOnly || this.eip === false) {
          return;
        }
        this.editing = !this.editing;
        if (!this.editing) {
          if (this.AdjacentElementFilterFields.nodeList.length) {
            this.AdjacentElementFilterFields.current();
          } else {
            this.focus();
          }
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.browse.edit.cancel, {
      down: (event) => {
        if (this.grid) {
          return;
        }
        if (this.editing) {
          event.preventDefault();
          event.stopPropagation();
          this.editing = false;
          if (this.AdjacentElementFilterFields.nodeList.length) {
            this.AdjacentElementFilterFields.current();
          } else {
            this.focus();
          }
        }
        this.resetHotkeyState();
      }
    }, 'raw-listener');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('activateRow', this.handleActivateRow);
    this.removeEventListener('blur', this.handleBlur);
    this.removeEventListener('browseHeaderCellActivated', this.handleBrowseHeaderCellActivated);
    this.removeEventListener('browseHeaderCellAttached', this.handleBrowseHeaderCellAttached);
    this.removeEventListener('browseHeaderCellDetached', this.handleBrowseHeaderCellDetached);
    this.removeEventListener('browseHeaderCellFilterableChanged', this.handleBrowseHeaderCellFilterableChanged);
    this.removeEventListener('browsePageLoaded', this.handleBrowsePageLoaded);
    this.removeEventListener('click', this.enterEditMode);
    this.removeEventListener('columnJustifyChanged', this.handleColumnJustifyChanged);
    this.removeEventListener('columnWidthChanged', this.handleColumnWidthChanged);
    this.removeEventListener('filterInputBlurred', this.handleFilterInputBlurred);
    this.removeEventListener('focus', this.handleFocus);
    this.removeEventListener('focusElement', this.handleFocusElement);
    this.removeEventListener('keliBlur', this.handleEditableFieldBlur);
    this.removeEventListener('keliMultiSelectChanged', this.handleKeliMultiSelectChanged);
    this.removeEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.removeEventListener('setSelectedRow', this.handleSetSelectedRow);
    this.removeEventListener('sortByColumn', this.handleSortByColumn);

    if (this.AdjacentElementFilterFields) {
      this.AdjacentElementFilterFields.destroy();
      this.AdjacentElementFilterFields = null;
    }
    this.selectedRow = null;
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  activateShortcutKey() {
    this.dispatchEvent(new CustomEvent('focusElement', {detail: {element: this}}));
  }

  getPageInfo() {
    if (this.resizeToContent) {
      return {size: (this.rows || 0), top: 0};
    }
    if (this.multiLine) {
      return {
        size: Math.ceil(this.$.browseBodyScroll.clientHeight / this.rowHeight),
        top: this.$.controller.getViewportTop(this.$.browseBodyScroll.scrollTop)
      };
    }
    return {
      size: Math.ceil(this.$.browseBodyScroll.clientHeight / this.rowHeight),
      top: Math.floor(this.$.browseBodyScroll.scrollTop / this.rowHeight)
    };
  }

  enterEditMode(event) {
    if (this.multiLine || this.readOnly || this.eip === false || (!event.altKey && !this.editing)) {
      return;
    }
    const target = getEventTarget(event);
    if (target.classList.contains('cell')) {
      this.$.editRow.targetKeliName = target.dataset.keliname;
      this.selectedRow = target.parentElement;
    }
    this.editing = true;
  }

  setTableWidth() {
    this._setTableWidthJob = Debouncer.debounce(this._setTableWidthJob, timeOut.after(10), () => {
      const columns = this.$.header.querySelectorAll('keli-browse-header-cell');
      let totalWidth = 0;
      for (let column of columns) {
        totalWidth += column.width || parseInt(window.getComputedStyle(column, null).getPropertyValue('width'), 10);
      }
      this.$.header.style.width = `${totalWidth + 100}px`;
      this.$.body.style.width = `${totalWidth}px`;
      this.$.footer.style.width = `${totalWidth + 100}px`;
    });
  }

  getColumnSortInfo(column) {
    return {
      field: column.keliname,
      direction: (column.sortDirection === column.Sort.ASCENDING ? 'ASC' : 'DESC')
    };
  }

  setSortOrder(sortValues) {
    if (sortValues) {
      this.currentSortOrder = sortValues;
    } else {
      sortValues = this.currentSortOrder;
    }
    const allColumns = this.$.header.querySelectorAll('keli-browse-header-cell');
    const sortedColumns = this.$.header.querySelectorAll('keli-browse-header-cell[sortdirection]');

    for (let [index] of sortedColumns.entries()) {
      sortedColumns[index].sortDirection = false;
      sortedColumns[index].sortIndex = null;
    }
    if (!sortValues) {
      return;
    }

    for (let [index, value] of sortValues.entries()) {
      let finalColumn = null;
      for (let column of allColumns) {
        if (column.keliname === value.field || (value.table && column.keliname === `${value.table}.${value.field}`)) {
          finalColumn = column;
          break;
        }
      }
      if (!finalColumn) {
        continue;
      }
      finalColumn.sortDirection = (value.direction === 'DESC' ? finalColumn.Sort.DESCENDING : finalColumn.Sort.ASCENDING);
      finalColumn.sortIndex = index;
    }
  }

  moveFilterFieldFocus(columnName) {
    const filterField = this.AdjacentElementFilterFields.nodeList.find(element => element.keliname === columnName);
    filterField?.focusElement();
  }

  movePosition(direction, page, position) {
    let movingToPosition = null;

    if (!this.selectedRowNumber) {
      this.selectedRowNumber = 1;
    } else {
      if (position) {
        movingToPosition = position;
      } else if (page) {
        const pageInfo = this.getPageInfo();
        let pageToRow = (direction > 0 ? (this.selectedRowNumber + pageInfo.size) : (this.selectedRowNumber - pageInfo.size));
        pageToRow = (pageToRow < 1 ? 1 : pageToRow);
        pageToRow = (pageToRow > this.rows ? this.rows : pageToRow);
        movingToPosition = (pageToRow < 1 ? 1 : pageToRow);
      } else {
        let moveToRow = this.selectedRowNumber + (direction || 0);
        moveToRow = (moveToRow < 1 ? 1 : moveToRow);
        moveToRow = (moveToRow > this.rows ? this.rows : moveToRow);
        movingToPosition = moveToRow;
      }
      this.selectedRowNumber = movingToPosition;
      afterNextRender(this.$.body, () => {
        this.scrollToActiveSelection();
      });
    }
  }

  updateBrowseHeight(originalHeight = 0) {
    const newHeight = this.$.controller.getHeightOfBrowse();
    const newHeightStyle = `${newHeight}px`;
    const property = (this.multiLine ? 'minHeight' : 'height');

    if (this.$.body.style[property] !== newHeightStyle) {

      // Compensate for rows rendering above the viewport we're looking at, which can push down the rows we can see.
      if (originalHeight && originalHeight !== newHeight) {
        this.$.browseBodyScroll.scrollBy({
          top: newHeight - originalHeight,
          behavior: 'instant'
        });
      }

      this.$.body.style[property] = newHeightStyle;
    }
    return newHeight;
  }

  rowsChanged(newValue) {
    const newHeight = this.updateBrowseHeight();

    if (!newValue) {
      this.selectedRow = null;
    }

    this.$.blankSlate.rows = newValue;

    afterNextRender(this, () => {
      const newPageInfo = this.getPageInfo();
      if (this.resizeToContent) {
        this.dispatchEvent(new CustomEvent('browseResized', {detail: {pageInfo: newPageInfo}}));
      } else if (this.loadedPageInfo) {
        const oldPageInfo = this.loadedPageInfo;
        if (newPageInfo.size !== (oldPageInfo && oldPageInfo.size)) {
          this.dispatchEvent(new CustomEvent('browseResized', {detail: {pageInfo: newPageInfo}}));
        }
      }
    });
  }

  selectedRowChanged(newValue, oldValue) {
    let currentRowId;
    if (oldValue) {
      oldValue.selected = false;
    }
    if (newValue) {
      currentRowId = newValue.rowId;
      newValue.selected = true;
      if (this.editing) {
        this.$.editRow.row = newValue;
      }
    }

    this.$.controller.updateLastSelectedRow(currentRowId);
    this.dispatchEvent(new CustomEvent('keliBrowseRowSelected', {detail: {rowid: currentRowId}}));
  }

  async selectedRowNumberChanged(newValue) {
    this.currentlyMovingToSelectedRow = true;
    if (newValue) {
      await this.updateSelection();
    }
  }

  widthChanged(newValue) {
    this.style.webkitFlexBasis = newValue;
    this.style.flexBasis = newValue;
  }

  multiLineChanged(newValue) {
    this.updateStyles({
      '--keli-browse-row-height': (newValue ? 'auto' : ''),
      '--keli-browse-row-white-space': (newValue ? 'normal' : '')
    });
  }

  blankSlateChanged(newValue) {
    if (this.rows === undefined) {
      return;
    }
    this.$.blankSlate.enabled = newValue !== false;
  }

  handleAddBrowseRowClick(event) {
    event.stopPropagation();

    // Prevent more than one action in a sequence
    if (event?.repeat || event?.detail > 1) {
      return;
    }

    if (this.$.controller) {
      this.$.controller.dispatchEvent(new CustomEvent('browseControlCreate'));
    }
  }

  handleRemoveBrowseRowClick(event) {
    if (!this.dataSettled) {
      return;
    }

    event.stopPropagation();

    // Prevent more than one action in a sequence
    if (event?.repeat || event?.detail > 1) {
      return;
    }

    if (this.$.controller) {
      this.$.controller.dispatchEvent(new CustomEvent('browseControlDelete'));
    }
  }

  browseControlCreateVisibleChanged(newValue) {
    this.$.addBrowseRow.classList.toggle('visible', newValue);
  }

  browseControlDeleteVisibleChanged(newValue) {
    this.$.removeBrowseRow.classList.toggle('visible', newValue);
  }

  disableBrowseConfigurationChanged(newValue) {
    this.$.footerControls.classList.toggle('hidden', newValue);
  }

  editingChanged(newValue) {
    if (!this.$.editRow.keliname) {
      this.$.editRow.keliname = this.keliname + '.browseeditrow';
      this.$.editRow.browse = this;
      this.$.controller.createEditableBrowseCells(this.$.editRow, 'eipCell');
    }
    if (newValue) {
      this.$.editRow.row = this.selectedRow;
    }
    this.$.editRow.editing = newValue;
  }

  filterableChanged(newValue) {
    const cellList = this.$.header.querySelectorAll('#header keli-browse-header-cell');
    for (let cell of cellList) {
      cell.filterable = newValue;
    }
    this.classList.toggle('filterable', newValue);
  }

  focusedChanged(newValue) {
    this.classList.toggle('focused', newValue);
    if (newValue) {
      this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
        bubbles: true,
        composed: true,
        detail: {element: this}
      }));
      this.dispatchEvent(new CustomEvent('keliFocus', {bubbles: true, composed: true, detail: {element: this}}));
    } else {
      this.dispatchEvent(new CustomEvent('keliBlur', {bubbles: true, composed: true, detail: {element: this}}));
    }
    this.resetHotkeyState();
  }

  footerControlsChanged(newValue) {
    this.$.footerControls.classList.toggle('hidden', (newValue === false));
  }

  computeFormattedShortcutKey(shortcutKey) {
    if (KeliInterface.isTouchDevice) {
      return;
    }
    return (shortcutKey ? formatHotkeyAsText(shortcutKey) : undefined);
  }

  heightChanged(newValue) {
    if (this.resizeToContent) {
      return;
    }

    this.updateStyles({'--keli-browse-height': (newValue ? `${parseInt(newValue, 10)}px` : '')});
  }

  async updateSelection() {
    if (!this.selectedRowNumber) {
      this.selectedRowNumber = await this.$.controller.getDefaultPosition();
      return;
    }
    if (this.rows && this.selectedRowNumber > this.rows) {
      this.selectedRowNumber = this.rows;
      return;
    }

    afterNextRender(this.$.body, async () => {
      // We match all possible rows in case we accidentally have duplicated row elements.
      let matchingRows = this.$.body.querySelectorAll(`keli-browse-row[position="${this.selectedRowNumber}"]`);

      // See if the row is still in the dataset, just disconnected from the DOM.
      if (!matchingRows.length && this.selectedRow) {
        const rowIsLoaded = await this.$.controller.rowExistsInDataset(this.selectedRow.rowId);
        matchingRows = this.$.body.querySelectorAll(`keli-browse-row[position="${this.selectedRowNumber}"]`);
        if (rowIsLoaded && !matchingRows.length) {
          return;
        }
      }

      this.selectedRow = (matchingRows.length ? matchingRows[matchingRows.length - 1] : null);
    });
  }

  customScrollIntoView(rowElement) {
    // Javascript's scrollIntoView scrolls all ancestors until the element is visible.
    // We just want to scroll the browse until the row is visible in the browse viewport.
    const elementTop = rowElement.offsetTop;
    const elementBottom = elementTop + (this.selectedRow?.clientHeight || this.rowHeight);
    const top = this.$.browseBodyScroll.scrollTop;
    const height = this.$.browseBodyScroll.clientHeight;
    const bottom = (top + height);

    if (elementTop >= bottom) {
      this.$.browseBodyScroll.scrollTo({top: elementBottom - height, behavior: 'instant'});
    } else if (elementBottom <= top) {
      this.$.browseBodyScroll.scrollTo({top: elementTop, behavior: 'instant'});
    }
  }

  scrollToActiveSelection(multiLineReload = false) {
    if (this.resizeToContent && this.selectedRow) {
      this.customScrollIntoView(this.selectedRow);
      return;
    }

    const elementTop = this.$.controller.getPositionOffsetForRow(this.selectedRowNumber);
    const elementBottom = elementTop + (this.selectedRow?.clientHeight || this.rowHeight);
    const top = this.$.browseBodyScroll.scrollTop;
    const bottom = (top + this.$.browseBodyScroll.clientHeight);

    const rowIsInView = (elementTop <= bottom && elementBottom >= top);
    if (this.multiLine && !multiLineReload && rowIsInView) {
      return;
    }

    if (this.selectedRow?.position === this.selectedRowNumber) {
      this.customScrollIntoView(this.selectedRow);
      return;
    }

    if (elementTop <= top) {
      this.$.browseBodyScroll.scrollTo({
        top: elementTop,
        behavior: 'instant'
      });
    } else if ((elementTop + this.rowHeight) >= bottom) {
      this.$.browseBodyScroll.scrollTo({
        top: this.rowHeight + elementTop - (bottom - top),
        behavior: 'instant'
      });
    }
  }

  focusHeaderFilterIfInViewport() {
    if (!this.AdjacentElementFilterFields) {
      return;
    }

    let filterFieldElement = this.AdjacentElementFilterFields.element;
    if (!filterFieldElement || !filterFieldElement.isConnected) {
      filterFieldElement = this.AdjacentElementFilterFields.firstElement;
    }

    if (!filterFieldElement || KeliInterface.isTouchDevice) {
      this.focus();
      return;
    }

    let filterLeft = filterFieldElement && filterFieldElement.offsetLeft;
    let browseLeft = this.$.browseBodyScroll.scrollLeft;
    let browseRight = (browseLeft + this.$.browseBodyScroll.getBoundingClientRect().width);
    let scrolledOutOfView = false;
    let scrolledElement;
    let currentElement = this;
    while (currentElement.offsetParent) {
      currentElement = currentElement.offsetParent;
      if (currentElement.scrollTop) {
        scrolledElement = currentElement;
        break;
      }
    }
    if (scrolledElement && (this.offsetTop + filterFieldElement.offsetTop) < scrolledElement.scrollTop) {
      scrolledOutOfView = true;
    }

    if (!scrolledOutOfView && filterLeft >= browseLeft && filterLeft <= browseRight) {
      this.AdjacentElementFilterFields.current(filterFieldElement);
    } else {
      this.focus();
    }
  }

  resizeToContentChanged(newValue) {
    if (this.$.controller) {
      this.$.controller.loadAllRows = newValue;
    }
    this.classList.toggle('resizeToContent', newValue);
    this.resizableByUser = !newValue;

    if (newValue) {
      this.updateStyles({'--keli-browse-height': ''});
    }
  }

  readOnlyChanged(newValue) {
    if (newValue) {
      this.editing = false;
    }
  }

  resizableByUserChanged(newValue) {
    if (this.resizeToContent) {
      return;
    }
    this.$.resizeBottom.style.display = (newValue ? 'block' : 'none');
  }


  handleBrowseHeaderScroll() {
    this.synchronizeTableScrolling(this.$.browseHeaderScroll);
  }

  handleBrowseBodyScroll() {
    this.synchronizeTableScrolling(this.$.browseBodyScroll);
    this.publishScrolled();
  }

  handleBrowseFooterScroll() {
    this.synchronizeTableScrolling(this.$.browseFooterScroll);
  }

  synchronizeTableScrolling(scrollingElement) {
    if (scrollingElement.ignoreScrollEvent) {
      scrollingElement.ignoreScrollEvent = false;
      return;
    }

    const elements = [
      this.$.browseHeaderScroll,
      this.$.browseBodyScroll,
      this.$.browseFooterScroll
    ];

    for (const element of elements) {
      if (element !== scrollingElement) {
        element.ignoreScrollEvent = true;
        element.scrollLeft = scrollingElement.scrollLeft;
      }
    }

    // Safety check to make sure that all elements end up the same after scrolling
    this._synchronizeTableScrollingJob = Debouncer.debounce(this._synchronizeTableScrollingJob, timeOut.after(200), () => {
      for (const element of elements) {
        element.scrollLeft = this.$.browseBodyScroll.scrollLeft;
      }
    });
  }

  publishScrolled() {
    if (!this.browseScrolledLastPageInfo) {
      this.browseScrolledLastPageInfo = {top: 0};
    }
    const newPageInfo = this.getPageInfo();
    if (newPageInfo.top !== this.browseScrolledLastPageInfo.top) {
      this.browseScrolledLastPageInfo = newPageInfo;
      this.dispatchEvent(new CustomEvent('browseScrolled', {detail: {pageInfo: newPageInfo}}));
    }
  }

  handleBrowseHeaderCellFilterableChanged(event) {
    if (this.AdjacentElementFilterFields && (!this.AdjacentElementFilterFields.element || this.AdjacentElementFilterFields.element === event.detail.element)) {
      this.AdjacentElementFilterFields.element = undefined;
      this.focusHeaderFilterIfInViewport();
    }
  }

  handleColumnJustifyChanged(event) {
    const rows = this.$.body.querySelectorAll('keli-browse-row');
    for (let row of rows) {
      row.dispatchEvent(new CustomEvent('browseCellAttributes', {detail: event.detail}));
    }
  }

  handleColumnWidthChanged(event) {
    const footerCell = this.$.footer.querySelector(`[keliname="${event.detail.keliname}"]`);
    if (footerCell) {
      footerCell.width = event.detail.width;
    }

    const rows = this.$.body.querySelectorAll('keli-browse-row');
    for (let row of rows) {
      row.dispatchEvent(new CustomEvent('browseCellAttributes', {detail: event.detail}));
    }
    if (this.$.editRow) {
      this.$.editRow.dispatchEvent(new CustomEvent('browseCellAttributes', {detail: event.detail}));
    }
    this.setTableWidth();
    if (this.multiLine) {
      this.$.controller.updateAllRowHeights();
    }
  }

  handleBrowseHeaderCellAttached(event) {
    if (this.filterable !== undefined) {
      event.detail.cell.filterable = this.filterable;
    }
    this.setSortOrder();

    this._handleFocusHeaderFilterIfInViewportJob = Debouncer.debounce(this._handleFocusHeaderFilterIfInViewportJob, timeOut.after(10), () => {
      if (this.focused) {
        this.focusHeaderFilterIfInViewport();
      }
    });
  }

  handleBrowseHeaderCellDetached(event) {
    if (this.AdjacentElementFilterFields && this.AdjacentElementFilterFields.element === event.detail.element) {
      this.AdjacentElementFilterFields.element = undefined;
      this.focusHeaderFilterIfInViewport();
    }
  }

  handleBrowseHeaderCellActivated(event) {
    if (this.AdjacentElementFilterFields) {
      this.AdjacentElementFilterFields.element = event.detail.element;
    }
  }

  async handleBrowsePageLoaded(event) {
    this.$.blankSlate.enabled = (this.blankSlate !== false);

    if (!event.detail?.visualOnly) {
      this.currentlyFiltering = false;
      this.currentlySorting = false;
      return;
    }

    await this.updateSelection();
    afterNextRender(this, async () => {
      this.setTableWidth();
      if (this.chooseOnFilteringComplete) {
        if (this.selectedRow) {
          this.selectedRow.activateRow(event);
        }
        this.chooseOnFilteringComplete = false;
      }
      if (this.multiLine && (event.detail?.reloaded || this.currentlyMovingToSelectedRow)) {
        this.scrollToActiveSelection(true);
      }
      this.currentlyMovingToSelectedRow = false;
    });
  }

  handleFocusElement(event) {
    const target = getEventTarget(event);
    if (target !== this) {
      return;
    }

    if (this.editing) {
      if (event.detail && event.detail.last) {
        this.$.editRow.AdjacentElement.last();
      } else {
        this.$.editRow.AdjacentElement.current();
      }
      return;
    }

    if (KeliInterface.isTouchDevice || (!this.AdjacentElementFilterFields || this.AdjacentElementFilterFields.nodeList.length === 0)) {
      this.focus();
    } else {
      this.focusHeaderFilterIfInViewport();
    }
  }

  handleElementResized() {
    if (this.$.modifyColumnsMenu) {
      this.$.modifyColumnsMenu.style.height = `${this.clientHeight - 10}px`;
    }
    if (!this.resizeToContent) {
      this._handleElementResizedJob = Debouncer.debounce(this._handleElementResizedJob, timeOut.after(100), () => {
        this.dispatchEvent(new CustomEvent('browseResized', {detail: {pageInfo: this.getPageInfo()}}));
      });
    }
  }

  handleResizableResizeElement(event) {
    event.stopPropagation();
    if (event.detail.height) {
      this.style.height = this.style.maxHeight = `${parseInt(event.detail.height, 10)}px`;
    }
  }

  handleResizableResizeElementEnded(event) {
    event.stopPropagation();
    if (event.detail.height) {
      this.height = event.detail.height;
      this.style.height = this.style.maxHeight = '';
    }
  }

  handleFocus() {
    this.focused = true;
  }

  handleBlur() {
    this.focused = false;
  }

  handleEditableFieldBlur() {
    this.resetHotkeyState();
  }

  handleKeliMultiSelectChanged() {
    this.focusHeaderFilterIfInViewport();
  }

  handleSetPanelActiveElement(event) {
    if (event.detail.element !== this) {
      event.stopPropagation();
      this.handleFocus();
    }
  }

  handleFilterInputBlurred() {
    this.resetHotkeyState();
  }

  handleSetSelectedRow(event) {
    this.selectedRowNumber = event.detail.element.position;
    this.focusHeaderFilterIfInViewport();
  }

  handleSortByColumn(event) {
    if (!this.sortable) {
      return;
    }
    const sortInfo = event.detail;
    const sortDirection = (sortInfo.column.sortDirection === sortInfo.column.Sort.ASCENDING ? sortInfo.column.Sort.DESCENDING : sortInfo.column.Sort.ASCENDING);
    sortInfo.column.sortDirection = sortDirection;

    const sortedColumns = this.$.header.querySelectorAll('#header keli-browse-header-cell[sortdirection]');

    let finalSort = [];
    for (let i = 0, count = sortedColumns.length; i < count; ++i) {
      const column = sortedColumns[i];
      if (column !== sortInfo.column && sortInfo.reset) {
        column.sortDirection = false;
        column.sortIndex = null;
        continue;
      }

      if (column.sortIndex !== null) {
        finalSort[column.sortIndex] = this.getColumnSortInfo(column);
      }
    }

    if (sortDirection && [undefined, null].includes(sortInfo.column.sortIndex)) {
      sortInfo.column.sortIndex = finalSort.length;
      finalSort.push(this.getColumnSortInfo(sortInfo.column));
    }

    finalSort = finalSort.filter(() => true); // Converts the sparse array to contiguous
    this.currentlySorting = true;
    this.dispatchEvent(new CustomEvent('setupBrowseOrder', {detail: finalSort}));
  }

  handleModifyColumnsClick() {
    this.$.modifyColumnsMenu.style.height = `${this.clientHeight - 10}px`;
    this.$.modifyColumnsMenu.browse = this;
    this.$.modifyColumnsMenu.fromElement = this.$.modifyColumns;
    this.$.modifyColumnsMenu.fromElementPosition = {bottom: true, right: true};
    this.$.modifyColumnsMenu.opened = !this.$.modifyColumnsMenu.opened;
  }

  handleActivateRow(event) {
    if (this.focused) {
      if (event.detail.element.animate) {
        const rowWidth = event.detail.element.clientWidth;
        const scaleFactor = (rowWidth - 6) / rowWidth;
        event.detail.element.animate([
          {transform: 'scaleX(1)'},
          {transform: `scaleX(${scaleFactor})`},
          {transform: 'scaleX(1)'},
        ], {duration: 100, iterations: 1, easing: 'cubic-bezier(0.1, 0.7, 1.0, 0.1)'});
      }

      this.dispatchEvent(new CustomEvent('keliBrowseRowActivated', {detail: event.detail}));
    }
  }

  dispatchDataSettled(newValue) {
    this.dispatchEvent(new CustomEvent('browseDataSettledChanged', {
      bubbles: true, composed: true,
      detail: {
        settled: newValue,
        control: this.keliname
      }
    }));
  }

  computeDataSettled(currentlyFiltering, currentlySorting, selectedRow) {
    return (!currentlyFiltering && !currentlySorting && !!selectedRow);
  }

  _keliAccept(event, isClosingPanel) {
    if (!isClosingPanel) {
      if (this.AdjacentElementFilterFields?.currentElement?._currentValueChanged?.isActive()) {
        this.AdjacentElementFilterFields.currentElement._currentValueChanged.flush();
      }
      return;
    }
    if (this.editing) {
      this.editing = false;
    }
  }
}

customElements.define(KeliBrowse.is, KeliBrowse);
export {KeliBrowse};
